import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createRole, updateRole, deleteRole, getPortfolios, createPortfolios, getPortfolioDetail, deletePortfolio, getPayments, createPayments, createPayoff, printPayoff, createPromisePay, getPromisePayments, deletePromisePay, createSideNote } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetPayments = (id) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => getPayments(id),
  });

  return { data, isLoading, isSuccess, isError, error };
};

export const useGetPortfolioDetail = (id) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['portfolio', id],
    queryFn: () => getPortfolioDetail(id),
  });

  return { data, isLoading, isSuccess, isError, error };
};

export const useGetPromisePayments = (id) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['promise-payments', id],
    queryFn: () => getPromisePayments(id),
  });

  return { data, isLoading, isSuccess, isError, error };
};
export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createPayments(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payments"] });
      showNotification('success', 'Payment created successfully!');
    },
    onError: (error) => {
      console.error("Error creating payment:", error);
      let errorMessage = 'Error creating payment. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const useCreatePayoff = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createPayoff(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payments"] });
      showNotification('success', 'Payoff complete successfully!');
    },
    onError: (error) => {
      console.error("Error completing payoff:", error);
      let errorMessage = 'Error completing payoff. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const usePrintPayoff = () => {
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => printPayoff(data),
    onSuccess: async () => {
      showNotification('success', 'Payoff quote printed successfully!');
    },
    onError: (error) => {
      console.error("Error printing payoff:", error);
      let errorMessage = 'Error printing payoff. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const useCreatePromisePay = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createPromisePay(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["promise-payments"] });
      showNotification('success', 'Promise pay created successfully!');
    },
    onError: (error) => {
      console.error("Error creating promise pay:", error);
      let errorMessage = 'Error creating promise pay. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const useDeletePromisePay = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (id) => deletePromisePay(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["promise-payments"] });
      showNotification('success', 'Promise pay deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting promise pay:", error);
      let errorMessage = 'Error deleting promise pay. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateRole(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });

      // Show success notification
      showNotification('success', 'Role updated successfully!');

      // Navigate immediately without setTimeout
      navigate('/roles');
    },
    onError: (error) => {
      console.error("Error updating role:", error);

      // Show error notification
      let errorMessage = 'Error updating role. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useDeletePortfolio = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deletePortfolio(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["portfolios"] });

      // Show success notification
      showNotification('success', 'Portfolio deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting role:", error);

      // Show error notification
      let errorMessage = 'Error deleting role. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useCreateSideNote = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createSideNote(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payments"] }); // side-notes might affect payments or calculus
      // also maybe invalidate "calculus-detail" as seen in AccountsRightDrawer.js
      showNotification('success', 'Side note created successfully!');
    },
    onError: (error) => {
      console.error("Error creating side note:", error);
      let errorMessage = 'Error creating side note. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};